/**
 * In-memory candle history for live charts, per price service market and
 * interval, so reopening a chart or switching back to an interval reuses
 * what was already loaded. Also remembers, for the session, which market and
 * interval pairs returned no candles at all.
 */

import type { CandlePoint } from "@/lib/live-candles/candle.types";
import type { ServiceExchange } from "./exchanges";
import type { ServiceInterval } from "./intervals";

/** Cached history is reused for this long after it was last kept current. */
export const CANDLE_CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_ENTRIES = 24;

export interface CachedCandles {
  candles: CandlePoint[];
  hasMoreHistory: boolean;
  /** When the candles were last known to be current (ms). */
  savedAt: number;
}

const cache = new Map<string, CachedCandles>();
const emptyMarkets = new Set<string>();

export function candleCacheKey(
  exchange: ServiceExchange,
  symbol: string,
  interval: ServiceInterval
): string {
  return `${exchange}:${symbol}:${interval}`;
}

export function getCachedCandles(
  key: string,
  now = Date.now()
): CachedCandles | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (now - entry.savedAt > CANDLE_CACHE_TTL_MS || entry.candles.length === 0) {
    cache.delete(key);
    return null;
  }
  return entry;
}

export function setCachedCandles(
  key: string,
  candles: CandlePoint[],
  hasMoreHistory: boolean,
  now = Date.now()
): void {
  if (candles.length === 0) return;
  // Re-inserting keeps the Map in least recently saved order.
  cache.delete(key);
  cache.set(key, { candles, hasMoreHistory, savedAt: now });
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

/** The market returned no candles for this interval; not asked again this session. */
export function markCandlesEmpty(key: string): void {
  emptyMarkets.add(key);
}

export function hasNoCandles(key: string): boolean {
  return emptyMarkets.has(key);
}

/** Tests only. */
export function resetCandleCache(): void {
  cache.clear();
  emptyMarkets.clear();
}
